/**
 * Shared required-field/group completion rules for assessment forms.
 *
 * Extracted from RequiredFieldsWidget so the same rules drive both the in-form widget and
 * out-of-form checks (e.g. warning before marking a service Performed from the calendar).
 *
 * `values` is the flat { [fieldId]: value } map the form uses (the shape returned by
 * GetFieldValues / used as Formik initialValues).
 */

/**
 * Walk data.validation to collect the IDs of all required fields.
 * generateValidationSchema uses Object.values(data).map(item => Object.values(item))
 * so data.validation is an array of objects (one per "group"), each mapping field name → field config.
 */
export function collectRequiredIds(data) {
  const ids = new Set();
  const validation = data?.validation;
  if (!validation || typeof validation !== "object") return ids;

  // Support both shapes:
  //   flat object  { "7487": { validationType: "required", name: 7487 }, ... }
  //   array of group-objects  [{ fieldName: { validationType, name }, ... }, ...]
  const fieldEntries = Array.isArray(validation)
    ? validation.flatMap((group) =>
        group && typeof group === "object" ? Object.values(group) : []
      )
    : Object.values(validation);

  fieldEntries.forEach((field) => {
    if (!field) return;
    const isRequired =
      field.requiredErrorMsg ||
      field.validationType === "required" ||
      field.validationType === "email" ||
      field.validationType === "phone" ||
      field.validationType === "date" ||
      field.validationType === "zipcod";
    if (isRequired) ids.add(String(field.name));
  });
  return ids;
}

/** All field ids under a group/section, recursively (for required-group satisfaction). */
export function collectFieldIdsDeep(node, acc = []) {
  (node?.formFields ?? []).forEach((f) => acc.push(String(f.id)));
  (node?.groups ?? []).forEach((g) => collectFieldIdsDeep(g, acc));
  return acc;
}

/**
 * Walk data.sections to build a list of sections containing required items:
 * required fields AND required groups (a required group/section is satisfied when
 * at least one field inside it has a value).
 */
export function buildSectionDefs(data, requiredIds) {
  const sections = [];
  (data?.sections ?? []).forEach((section) => {
    // "Per client" sections are completed per attended client in the session panel, not in the
    // shared form, so they don't count toward the shared form's required items.
    if (section.isPerClient) return;
    const items = [];

    // Required group items — the section itself and any nested group flagged required.
    const addRequiredGroups = (node) => {
      (node?.groups ?? []).forEach((g) => {
        if (g.isPerClient) return;
        if (g.required) {
          items.push({
            kind: "group",
            id: `group-${g.id}`,
            label: g.name || `Group ${g.id}`,
            fieldIds: collectFieldIdsDeep(g),
          });
        }
        addRequiredGroups(g);
      });
    };
    if (section.required) {
      items.push({
        kind: "group",
        id: `group-${section.id}`,
        label: section.name || `Section ${section.id}`,
        fieldIds: collectFieldIdsDeep(section),
      });
    }
    addRequiredGroups(section);

    // Required field items (section + nested groups, recursively).
    const addRequiredFields = (node) => {
      (node?.formFields ?? []).forEach((f) => {
        if (requiredIds.has(String(f.id))) {
          items.push({ kind: "field", id: String(f.id), label: f.name || `Field ${f.id}` });
        }
      });
      (node?.groups ?? []).forEach((g) => { if (!g.isPerClient) addRequiredFields(g); });
    };
    addRequiredFields(section);

    if (items.length > 0) {
      sections.push({ sectionId: section.id, sectionName: section.name, fields: items });
    }
  });
  return sections;
}

/** A field counts as answered when it has a non-empty, non-false value. */
export const hasValue = (v) => v !== undefined && v !== null && v !== "" && v !== false;

/**
 * Flat list of required items that are still unanswered, given a form definition and its values.
 * Returns [{ kind: "field"|"group", label, section }]. Empty array = everything required is complete.
 */
export function getIncompleteRequired(data, values) {
  const requiredIds = collectRequiredIds(data);
  const defs = buildSectionDefs(data, requiredIds);
  const missing = [];
  defs.forEach((s) => {
    s.fields.forEach((f) => {
      const filled =
        f.kind === "group"
          ? (f.fieldIds || []).some((fid) => hasValue(values?.[fid]))
          : hasValue(values?.[f.id]);
      if (!filled) {
        missing.push({ kind: f.kind, label: f.label, section: s.sectionName });
      }
    });
  });
  return missing;
}

/**
 * Required signatures that haven't been signed yet.
 * `signatures` = completed signature rows from the API; `requiredSignatures` = form definition.
 */
export function getMissingSignatures(requiredSignatures, signatures, roleMap = {}) {
  if (!Array.isArray(requiredSignatures) || requiredSignatures.length === 0) return [];
  const signed = new Set(
    (Array.isArray(signatures) ? signatures : []).map((s) => s.formRequiredSignatureId)
  );
  return requiredSignatures
    .filter((req) => !signed.has(req.id))
    .map((req) => ({
      kind: "signature",
      section: "Signatures",
      label:
        (req.roleId != null && roleMap[String(req.roleId)]) ||
        req.roleName ||
        req.name ||
        `Signature ${req.id}`,
    }));
}
